import { useState, useEffect, useCallback, useRef } from 'react';

export type AudioDevicesState = {
    hasMicrophone: boolean;
    hasSpeakers: boolean;
    hasPermission: boolean;
    isChecking: boolean;
    error: string | null;
    devices: {
        microphones: MediaDeviceInfo[];
        speakers: MediaDeviceInfo[];
    };
};

const initialState: AudioDevicesState = {
    hasMicrophone: false,
    hasSpeakers: false,
    hasPermission: false,
    isChecking: true,
    error: null,
    devices: {
        microphones: [],
        speakers: [],
    },
};

/**
 * Hook para verificar e monitorar dispositivos de áudio disponíveis
 * Usado para validar se o atendente pode atender chamadas
 */
export function useAudioDevices() {
    const [state, setState] = useState<AudioDevicesState>(initialState);
    const streamRef = useRef<MediaStream | null>(null);

    /**
     * Verifica dispositivos de áudio disponíveis
     * Primeiro tenta sem permissão, depois solicita se necessário
     */
    const checkDevices = useCallback(async (): Promise<AudioDevicesState> => {
        setState(prev => ({ ...prev, isChecking: true, error: null }));

        try {
            // Verificar suporte do navegador
            if (!navigator.mediaDevices?.enumerateDevices) {
                const errorState: AudioDevicesState = {
                    ...initialState,
                    isChecking: false,
                    error: 'Navegador não suporta detecção de dispositivos de áudio',
                };
                setState(errorState);
                return errorState;
            }

            // Primeiro, enumerar dispositivos sem solicitar permissão
            // Isso retorna dispositivos mas sem labels se não houver permissão
            let devices = await navigator.mediaDevices.enumerateDevices();

            let audioInputs = devices.filter(d => d.kind === 'audioinput');
            let audioOutputs = devices.filter(d => d.kind === 'audiooutput');

            // Se encontramos dispositivos mas sem labels, precisamos de permissão
            // para ter certeza de que são realmente acessíveis
            const needsPermission = audioInputs.some(d => !d.label) || audioInputs.length === 0;

            let hasPermission = !needsPermission;

            if (needsPermission && navigator.mediaDevices.getUserMedia) {
                try {
                    // Solicitar permissão de microfone
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    streamRef.current = stream;
                    hasPermission = true;

                    // Re-enumerar para obter informações completas
                    devices = await navigator.mediaDevices.enumerateDevices();
                    audioInputs = devices.filter(d => d.kind === 'audioinput');
                    audioOutputs = devices.filter(d => d.kind === 'audiooutput');

                    // Liberar o stream após verificar
                    stream.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                } catch (permError: any) {
                    // Permissão negada ou erro ao acessar dispositivo
                    const errorMessage = permError.name === 'NotAllowedError'
                        ? 'Permissão de microfone negada'
                        : permError.name === 'NotFoundError'
                            ? 'Nenhum microfone encontrado'
                            : `Erro ao acessar microfone: ${permError.message}`;

                    const errorState: AudioDevicesState = {
                        hasMicrophone: false,
                        hasSpeakers: audioOutputs.length > 0,
                        hasPermission: false,
                        isChecking: false,
                        error: errorMessage,
                        devices: {
                            microphones: [],
                            speakers: audioOutputs,
                        },
                    };
                    setState(errorState);
                    return errorState;
                }
            }

            const resultState: AudioDevicesState = {
                hasMicrophone: audioInputs.length > 0,
                hasSpeakers: audioOutputs.length > 0 || true, // Assume speakers se não puder detectar
                hasPermission,
                isChecking: false,
                error: null,
                devices: {
                    microphones: audioInputs,
                    speakers: audioOutputs,
                },
            };

            setState(resultState);
            return resultState;
        } catch (error: any) {
            const errorState: AudioDevicesState = {
                ...initialState,
                isChecking: false,
                error: `Erro ao verificar dispositivos: ${error.message}`,
            };
            setState(errorState);
            return errorState;
        }
    }, []);

    /**
     * Verifica se o atendente pode atender chamadas
     * Retorna true se tem microfone e permissão
     */
    const canHandleCalls = useCallback((): boolean => {
        return state.hasMicrophone && state.hasPermission && !state.isChecking;
    }, [state]);

    /**
     * Retorna a mensagem de aviso apropriada se não puder atender chamadas
     */
    const getWarningMessage = useCallback((): string | null => {
        if (state.isChecking) return null;

        if (!state.hasMicrophone) {
            return 'Nenhum microfone detectado. Você não poderá atender chamadas de voz.';
        }

        if (!state.hasPermission) {
            return 'Permissão de microfone necessária para atender chamadas de voz.';
        }

        if (state.error) {
            return state.error;
        }

        return null;
    }, [state]);

    // Verificar dispositivos na montagem
    useEffect(() => {
        checkDevices();

        // Limpar stream se existir ao desmontar
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
        };
    }, [checkDevices]);

    // Monitorar mudanças nos dispositivos
    useEffect(() => {
        if (!navigator.mediaDevices?.addEventListener) return;

        const handleDeviceChange = () => {
            console.log('[AudioDevices] Dispositivos alterados, re-verificando...');
            checkDevices();
        };

        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
        };
    }, [checkDevices]);

    return {
        ...state,
        checkDevices,
        canHandleCalls,
        getWarningMessage,
    };
}

export default useAudioDevices;
